import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";

/**
 * A quick answer expands in place; a detailed answer links to its own page.
 * The text is in the HTML either way — expanding is presentation, not loading.
 *
 * Audit T02/T03: the row is addressable (`#a-<slug>`) so a chatbot or related
 * link can open the exact answer, and the toggle exposes aria-expanded /
 * aria-controls against a panel that is genuinely hidden when collapsed.
 */
export const AnswerRow = ({ a, defaultOpen = false, highlight = false, onOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef(null);

  useEffect(() => {
    if (!defaultOpen) return;
    setOpen(true);
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    return () => clearTimeout(t);
  }, [defaultOpen]);

  if (a.url) {
    return (
      <div ref={ref} id={`a-${a.slug}`} className="border-b border-slate-100 last:border-0 py-5 scroll-mt-24" data-testid={`ask-answer-${a.slug}`}>
        <Link to={a.url} className="group block">
          <span className="font-display font-bold text-navy group-hover:text-orange transition-colors block">
            {a.question}
          </span>
          <span className="text-slate-500 text-sm leading-relaxed block mt-1.5 max-w-3xl">{a.quick_answer}</span>
          <span className="inline-flex items-center gap-1.5 text-orange text-xs font-bold uppercase tracking-wide mt-3">
            Read the full answer <ArrowRight size={13} />
          </span>
        </Link>
      </div>
    );
  }

  const panelId = `panel-${a.slug}`;
  return (
    <div
      ref={ref}
      id={`a-${a.slug}`}
      className={`border-b border-slate-100 last:border-0 py-5 scroll-mt-24 transition-colors ${highlight ? "bg-orange/5 -mx-4 px-4 rounded-xl" : ""}`}
      data-testid={`ask-answer-${a.slug}`}
    >
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) onOpen?.(a.slug);
        }}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-start justify-between gap-4 text-left"
        data-testid={`ask-answer-toggle-${a.slug}`}
      >
        <span className="font-display font-bold text-navy">{a.question}</span>
        <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div id={panelId} hidden={!open}>
        <p className="text-slate-600 text-sm leading-relaxed max-w-3xl mt-3">{a.quick_answer}</p>
        {a.reviewer_line && <p className="text-[11px] text-slate-400 mt-2">{a.reviewer_line}</p>}
        {!a.reviewer_line && a.updated_line && <p className="text-[11px] text-slate-400 mt-2">{a.updated_line}</p>}
      </div>
    </div>
  );
};

export default AnswerRow;
